import React, { useEffect, useMemo, useState } from "react";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import Button from "@material-ui/core/Button";
import Grid from "@material-ui/core/Grid";
import Contact from "./contact";
import Service from "./service";
import CardTable from "./cardTable";
import { makeStyles } from "@material-ui/core/styles";
import ThankYouModal from "./thankYouModal";
import PayModal from "./payModal";
import GradingFeeModal from "./gradingFeeModal";
import Totals from "./totals";
import {
  quantities,
  years,
  evaluatePrice,
  serviceLevels,
  currencyCaption,
  evaluateSlabPrice,
} from "../enums";
import { createBlob } from "../ajax";

const useStyles = makeStyles((theme) => ({
  tabs: {
    marginBottom: theme.spacing() * 3,
  },
  link: {
    cursor: "pointer",
  },
  linkText: {
    color: "blue",
    textDecoration: "underline",
  },
  nextButton: { marginTop: theme.spacing() * 4 },
}));

export default () => {
  const classes = useStyles();
  const [closedGradingFeeModal, setClosedGradingFeeModal] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [service, setService] = useState(null);
  const [cards, setCards] = useState([]);
  const [contact, setContact] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    address: "",
    address2: "",
    city: "",
    state: "",
    zip: "",
    paymentMethod: "",
    paymentUser: "",
    paymentDiscount: 0,
  });

  const closePayment = useMemo(
    () => (showThankYou) => {
      setShowPayment(false);

      if (showThankYou === true) {
        setShowThankYou(true);
      }
    },
    []
  );

  const contactComplete = useMemo(
    () =>
      contact.firstName.length > 0 &&
      contact.lastName.length > 0 &&
      contact.email.length > 0 &&
      contact.phoneNumber.length > 0 &&
      contact.address.length > 0 &&
      contact.city.length > 0 &&
      contact.state.length > 0 &&
      contact.zip.length > 0,
    [contact]
  );

  const serviceComplete = useMemo(
    () => contactComplete && service !== null,
    [contactComplete, service]
  );

  const cardsComplete = useMemo(
    () =>
      serviceComplete &&
      cards.length > 0 &&
      !cards.some(
        (card) =>
          card.setName.length === 0 ||
          card.cardNumber.length === 0 ||
          card.playerName.length === 0
      ),
    [serviceComplete, cards]
  );

  const updateService = useMemo(() => (e) => setService(e.target.value), []);

  const submitContact = useMemo(
    () => (e) => {
      e.preventDefault();

      if (contactComplete) {
        setActiveTab(1);
      }
    },
    [contactComplete]
  );

  const submitService = useMemo(
    () => (e) => {
      e.preventDefault();

      if (serviceComplete) {
        setActiveTab(2);
      }
    },
    [serviceComplete]
  );

  const submitCards = useMemo(
    () => (e) => {
      e.preventDefault();

      if (cardsComplete) {
        setShowPayment(true);
      }
    },
    [cardsComplete]
  );

  const updateActiveTab = useMemo(
    () => (e, newActiveTab) => setActiveTab(newActiveTab),
    []
  );

  const updateContactFactory = (fieldName) => (e) => {
    const value = e.target.value;

    setContact((contact) => ({ ...contact, [fieldName]: value }));
  };

  const updateContactFirstName = useMemo(
    () => updateContactFactory("firstName"),
    []
  );

  const updateContactLastName = useMemo(
    () => updateContactFactory("lastName"),
    []
  );

  const updateContactEmail = useMemo(() => updateContactFactory("email"), []);

  const updateContactPhoneNumber = useMemo(
    () => updateContactFactory("phoneNumber"),
    []
  );

  const updateContactAddress = useMemo(
    () => updateContactFactory("address"),
    []
  );

  const updateContactAddress2 = useMemo(
    () => updateContactFactory("address2"),
    []
  );

  const updateContactCity = useMemo(() => updateContactFactory("city"), []);

  const updateContactZip = useMemo(() => updateContactFactory("zip"), []);

  const updateContactState = useMemo(() => updateContactFactory("state"), []);

  const updatePaymentMethod = useMemo(
    () => updateContactFactory("paymentMethod"),
    []
  );

  const updatePaymentUser = useMemo(
    () => updateContactFactory("paymentUser"),
    []
  );

  const updatePaymentDiscount = useMemo(
    () => updateContactFactory("paymentDiscount"),
    []
  );

  const addCard = useMemo(
    () => () =>
      setCards((cards) => [
        ...cards,
        {
          id: new Date().getTime(),
          quantity: 1,
          year: years[years.length - 1],
          setName: "",
          cardNumber: "",
          playerName: "",
          parallel: "",
          cardValue: 0,
          evaluationType: 0,
        },
      ]),
    []
  );

  const updateCardFactory =
    (formatter, targetProp) =>
    (fieldName) =>
    (id) =>
    ({ target }) => {
      const value = formatter(target[targetProp]);

      setCards((cards) => {
        const indexToUpdate = cards.findIndex((card) => card.id === id);

        return [
          ...cards.slice(0, indexToUpdate),
          { ...cards[indexToUpdate], [fieldName]: value },
          ...cards.slice(indexToUpdate + 1),
        ];
      });
    };

  const getUpdateMethods = useMemo(
    () => (id) => ({
      removeCard: () =>
        setCards((cards) => cards.filter((card) => card.id !== id)),
      updateQuantity: updateCardFactory(
        (value) => parseInt(value, 10),
        "value"
      )("quantity")(id),
      updateYear: updateCardFactory(
        (value) => parseInt(value, 10),
        "value"
      )("year")(id),
      updateSetName: updateCardFactory((value) => value, "value")("setName")(
        id
      ),
      updateCardNumber: updateCardFactory(
        (value) => value,
        "value"
      )("cardNumber")(id),
      updatePlayerName: updateCardFactory(
        (value) => value,
        "value"
      )("playerName")(id),
      updateParallel: updateCardFactory((value) => value, "value")("parallel")(
        id
      ),
      updateCardValue: updateCardFactory(
        (value) => Math.max(parseFloat(value) || 0, 0),
        "value"
      )("cardValue")(id),
      updateCardEvaluationType: updateCardFactory(
        (value) => parseInt(value, 10),
        "value"
      )("evaluationType")(id),
    }),
    []
  );

  const cardsToEvaluatePrice = useMemo(
    () =>
      cards
        .filter((card) => card.evaluationType === 1)
        .reduce((sum, card) => sum + card.quantity, 0) *
        evaluatePrice +
      cards
        .filter((card) => card.evaluationType === 2)
        .reduce((sum, card) => sum + card.quantity, 0) *
        evaluateSlabPrice,
    [cards]
  );

  const cardsToNotEvaluatePrice = useMemo(
    () =>
      service !== null
        ? serviceLevels[service].price *
          cards
            .filter((card) => card.evaluationType === 0)
            .reduce((sum, card) => sum + card.quantity, 0)
        : 0,
    [service, cards]
  );

  const getBlobUrl = useMemo(
    () => () =>
      new Promise((resolve, reject) =>
        createBlob({
          cards,
          contact,
          service,
          serviceLevels,
          cardsToEvaluatePrice,
          cardsToNotEvaluatePrice,
          currencyCaption,
        }).then(
          (key) =>
            resolve({
              url: `${window.location.protocol}//${window.location.host}/blob/${key}`,
              key,
            }),
          reject
        )
      ),
    [cards, contact, service, cardsToEvaluatePrice, cardsToNotEvaluatePrice]
  );

  const submitButton = (
    <Grid container className={classes.nextButton} justify="flex-end">
      <Grid item xs={12} md={4}>
        <Button
          type="submit"
          variant="outlined"
          color="primary"
          fullWidth
          disabled={showPayment}
        >
          {activeTab === 2 ? "Enter Payment" : "Continue"}
        </Button>
      </Grid>
    </Grid>
  );

  const onDone = useMemo(() => () => window.location.assign("/"), []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [activeTab]);

  return (
    <>
      <Tabs
        value={activeTab}
        onChange={updateActiveTab}
        variant="fullWidth"
        indicatorColor="primary"
        textColor="primary"
        className={classes.tabs}
      >
        <Tab label="Shipping" />
        <Tab label="Service" disabled={!contactComplete} />
        <Tab label="Cards" disabled={!serviceComplete} />
      </Tabs>
      <div>
        {activeTab === 0 ? (
          <form onSubmit={submitContact}>
            <Contact
              {...{
                contact,
                updateContactEmail,
                updateContactPhoneNumber,
                updateContactFirstName,
                updateContactLastName,
                updateContactAddress,
                updateContactAddress2,
                updateContactCity,
                updateContactState,
                updateContactZip,
              }}
            />
            {submitButton}
          </form>
        ) : null}
        {activeTab === 1 ? (
          <form onSubmit={submitService}>
            <Service
              {...{
                service,
                updateService,
                serviceLevels,
                currencyCaption,
              }}
            />
            {submitButton}
          </form>
        ) : null}
        {activeTab === 2 ? (
          <form onSubmit={submitCards}>
            <CardTable
              {...{
                cards,
                quantities,
                years,
                addCard,
                getUpdateMethods,
                currencyCaption,
              }}
            />
            <Totals
              {...{
                cardsToEvaluatePrice,
                cardsToNotEvaluatePrice,
                currencyCaption,
                paymentDiscount: contact.paymentDiscount,
              }}
            />
            {submitButton}
          </form>
        ) : null}
      </div>
      {showThankYou ? (
        <ThankYouModal
          {...{
            contact,
            total: cardsToEvaluatePrice + cardsToNotEvaluatePrice,
            getBlobUrl,
            close: onDone,
          }}
        />
      ) : null}
      <PayModal
        {...{
          open: showPayment,
          paymentMethod: contact.paymentMethod,
          updatePaymentMethod,
          paymentUser: contact.paymentUser,
          updatePaymentUser,
          updatePaymentDiscount,
          cardsToEvaluatePrice,
          cardsToNotEvaluatePrice,
          currencyCaption,
          onClose: closePayment,
        }}
      />
      <GradingFeeModal
        isOpen={activeTab === 1 && !closedGradingFeeModal}
        onClose={() => setClosedGradingFeeModal(true)}
      />
    </>
  );
};
